<template>
  <div class="single-select" :class="{ [bgTheme]: true }" v-click-outside="hideDropdown">
    <div
      class="field d-flex flex-wrap"
      ref="field"
      @keydown.enter="onEnter"
      @keydown.down.stop="selectNextItem"
      @keydown.esc="onEscape"
      @keydown.up.stop="selectPreviousItem"
      @keydown.delete="removeItem"
      :tabindex="value ? 0 : -1"
      @focus="toggleDropdown(true)"
      @keydown.tab="toggleDropdown(false)"
    >
      <with-close-icon
        item-wrapper-class="flex-grow-1 mw-0"
        class="d-flex align-items-center flex-grow-1 mw-0"
        :icon-alignment="dismissIconAlignment"
        @close="removeItem"
        :closeIconColor="closeIconColor"
        v-if="value"
      >
        <div class="chip text-medium" @click="toggleDropdown(!showDropdown)" v-no-capture>
          <template v-if="$scopedSlots.default">
            <!-- @slot This slot can be used to provide template to the selected item -->
            <slot v-bind:value="value"></slot>
          </template>
          <span class="pr-3 text-truncate" :title="value.label" v-else>{{ value.label }}</span>
        </div>
      </with-close-icon>
      <input
        class="text-medium plain"
        ref="search"
        v-model="searchText"
        type="text"
        @focus="onSearchFocus"
        @blur="onSearchBlur"
        :placeholder="placeholder"
        v-autofocus="autoFocus"
        v-else
      />
    </div>
    <div
      class="menu menu-shadow scroll-secondary"
      :class="{ 'd-none': !showDropdown }"
      :style="{ maxWidth: `${menuMaxWidth}px` }"
      @keydown.esc="toggleDropdown(false)"
      :data-preventBlur="true"
      ref="menu"
      tabindex="-1"
    >
      <div class="flex-center loader-wrapper" v-if="loading">
        <spinner />
      </div>
      <ul v-else>
        <li
          class="item selectable"
          v-for="(option, index) in dropdownOptions"
          :key="index"
          @click="selectItem(index)"
          :data-id="option.id"
          v-no-capture
        >
          <template v-if="$scopedSlots.listItem">
            <!-- @slot This slot can be used to provide template to the dropdown list items -->
            <slot name="listItem" v-bind:option="option"></slot>
          </template>
          <span class="text-truncate px-2" :title="option.label" v-else>{{ option.label }}</span>
        </li>
        <span class="item text-medium text-secondary" v-if="dropdownOptions.length === 0">
          <slot name="noResultMessage" v-if="$slots.noResultMessage"></slot>
          <template v-else>
            {{ noResultMessage }}
          </template>
        </span>
      </ul>
    </div>
  </div>
</template>

<script>
import WithCloseIcon from '@/components/WithCloseIcon'
import * as R from 'ramda'
import { createPopper } from '@popperjs/core'
import debounce from '@/utils/debounce'
import ListNavigationMixin from '@/mixins/ListNavigationMixin'
import Spinner from '@/components/Spinner'

/**
 * This component can be used as a searchable dropdown field.
 * The way selected item and Dropdown list items can be shown are customizable using slots
 */
export default {
  name: 'SingleSelect',
  props: {
    /**
     * It must be selected option if default template is getting used.
     * Else, it can be as per requirement. Depends on how consumer wants to use it in their template
     */
    value: [Object, Number, String],
    /**
     * The options to be shown in dropdown list. Each option should contain id, label
     */
    options: {
      default: () => [],
      type: Array
    },
    /**
     * placeholder for search field. It is only shown when there is no item selected
     */
    placeholder: String,
    loading: { type: Boolean, default: false },
    menuMaxWidth: { type: Number, default: 200 },
    dismissIconAlignment: {
      type: String,
      default: 'right' // possible values - top
    },
    noResultMessage: {
      type: String,
      default: 'Nothing here...'
    },
    /**
     * supported values - static | dynamic
     * static - means that search should be performed over supplied options
     * dynamic - callee will control the search. It may be controlled via API call. In such cases, component will emit 'search' event
     */
    searchType: {
      type: String,
      default: 'static'
    },
    justifyMenu: {
      type: String // supported values - '' | 'start' | 'end'
    },
    /**
     * Supported values - grey, document
     */
    bgTheme: {
      default: 'grey'
    },
    autoFocus: Boolean
  },
  components: {
    Spinner,
    WithCloseIcon
  },
  data() {
    return {
      searchText: '',
      showDropdown: false,
      closeIconColor: this.bgTheme === 'document' ? 'white' : this.bgTheme
    }
  },
  mixins: [ListNavigationMixin],
  computed: {
    dropdownOptions() {
      if (this.searchType === 'dynamic') {
        return this.options
      }
      return R.propOr([], 'options', this).filter(
        x => x.label.toLowerCase().indexOf(this.searchText.toLowerCase()) > -1
      )
    },
    isDropdownVisible() {
      return this.showDropdown
    }
  },
  methods: {
    onSearchFocus() {
      this.toggleDropdown(true)
    },
    onSearchBlur() {
      this.hideDropdown()
    },
    hideDropdown() {
      this.toggleDropdown(false)
    },
    toggleDropdown(show) {
      this.showDropdown = show
    },
    selectItem(index) {
      this.$emit('select', this.dropdownOptions[index])
      this.clearSearchField()
      this.hideDropdown()
    },
    clearSearchField() {
      setTimeout(() => (this.searchText = ''), 200) // we want to clear field after dropdown is closed
    },
    removeItem() {
      /** @emits
       * This event is emitted when an option is selected or removed
       */
      this.$emit('select', null)
      setTimeout(() => {
        this.$refs.search && this.$refs.search.focus()
      }, 100)
    },
    onEnter() {
      if (this.isDropdownVisible) {
        const isEmitted = this.emitSelected()
        if (isEmitted) {
          this.clearSearchField()
          this.$refs.field.focus()
          this.hideDropdown()
        }
      }
    },
    onEscape(event) {
      if (this.isDropdownVisible) {
        event.stopPropagation()
        this.hideDropdown()
      }
    },
    emitSearchEvent: debounce(function() {
      this.$emit('search', this.searchText)
    }, 200)
  },
  mounted() {
    let placement = 'bottom'
    if (this.justifyMenu) {
      placement += `-${this.justifyMenu}`
    }
    this.popper = createPopper(this.$refs.field, this.$refs.menu, {
      placement
    })
    this.toggleDropdown = debounce(this.toggleDropdown, 200)
  },
  watch: {
    showDropdown(show) {
      if (show) {
        this.popper.update()
      }
    },
    searchText() {
      if (this.searchType === 'dynamic') {
        this.emitSearchEvent()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/components/input';

.single-select {
  min-width: 0;
}

.loader-wrapper {
  height: 80px;
}

.menu {
  border-radius: 15px;
  min-width: 200px;
  background-color: white;
  padding: 10px 0;
  z-index: 1000;
  min-height: 80px;
  max-height: 300px;
}

ul {
  list-style: none;
  margin: 0;
  padding: 0;

  li {
    cursor: pointer;
  }
}

.item {
  display: flex;
  padding: 5px 10px;
}

.selectable:hover,
.selectable.selected {
  background-color: $quinary;
}

.chip {
  margin-bottom: 4px;
  padding: 3px 0;
  color: $primary;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.field {
  background-color: $quinary;
  border: 1px solid $tertiary;
  border-radius: 20px;
  padding: 3px 10px 0 10px;

  &:focus-within {
    outline: none;
    border-color: $prime;
  }
}
.single-select.document .field {
  background-color: $documentColor;
}

input {
  display: inline-block;
  height: 28px;
  margin-bottom: 3px;
  width: 100%;
}
</style>
